import {
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    inject,
    OnInit,
    Signal,
    signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import {
    AnimationsDataModel,
    ClientManagerInstanceToken,
    ClientManagerInterface,
    completedNotifications$,
    ConfigConstants,
    EventService,
    MaterialService,
    ModalClass,
    NotificationDetailState,
    NotificationIdState,
    NotificationModel,
    NotificationNavigationEntry,
    notificationRefreshRequired,
    NotificationStateUtilities,
    PaceCoreModule,
    Region,
    selectCentricarePCHEnabled,
    selectNotificationDetailById,
    selectNotificationNavigationEntries,
    stateToNotificationIdModel,
    UiResponsiveService,
    updateNotificationStatusFields,
    WindowService,
    YesNoModalDialogComponent,
    YesNoModalDialogModel,
} from '@evorait/pace-core';
import {
    PaceActionLink,
    PaceNotificationActionLinks,
    PaceNotificationActionLinksComponent,
    PaceNotificationDetailCarousel,
    PaceNotificationDetailCarouselComponent,
    PaceNotificationDetailField,
    PaceNotificationDetailHeader,
    PaceNotificationDetailHeaderAction,
    PaceNotificationDetailHeaderComponent,
    PaceNotificationDetailNavigationComponent,
    PaceNotificationLongtext,
    PaceNotificationLongtextComponent,
    PaceNotificationMaterialItem,
    PaceNotificationParticipantItem,
    PaceStatusBadgeComponent,
    PaceStatusStepButton,
    PaceStatusStepButtonComponent,
    PaceStatusStepEntry,
} from '@evorait/pace-components';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { faFlag } from '@fortawesome/free-solid-svg-icons';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { BsModalService } from 'ngx-bootstrap/modal';
import { merge, Observable, of } from 'rxjs';
import { distinctUntilChanged, filter, map, shareReplay, switchMap, take, tap } from 'rxjs/operators';
import { ConfirmationService } from '../../confirmation/services/confirmation.service';
import { SharedModule } from '../../shared/shared.module';
import { OverviewAnimations } from '../overview/overview-animations';
import { NOTIFICATION_DETAIL_ACTION, NotificationDetailsContentFactory } from './notification-details-content.factory';
import { NOTIFICATION_DETAIL_FIELD, NotificationDetailsHeaderFactory } from './notification-details-header.factory';
import { NotificationDetailsModalsService } from './notification-details-modals.service';
import { NotificationDetailsStatusStepService } from './notification-details-status-step.service';
import { NotificationDetailsStepButtonFactory } from './notification-details-step-button.factory';
import { NotificationModelFactory } from './notification-model.factory';

/** Everything the screen renders, assembled from the store in one place. */
interface NotificationDetailsView {
    header: PaceNotificationDetailHeader;
    longtext: PaceNotificationLongtext;
    carousel: PaceNotificationDetailCarousel;
    actionLinks: PaceNotificationActionLinks;
    stepButton: PaceStatusStepButton;
}

/** How far a slide travels when the user steps to the neighbouring notification. */
const SLIDE_OFFSET = 100;

/**
 * The notification-details screen: one notification's header, long texts, carousel and status step
 * button, with arrows stepping to its neighbours. Routed behind the
 * `environment.enableNgRx` flag.
 *
 * It reads the notification exclusively from the notification and notification-status store slices —
 * no `NotificationDataInterface` or `NotificationStatusInterface` anywhere, and no route resolver
 * either: the route parameters name the notification, everything else is selected, and the one
 * status field the screen writes (the unread flag) is dispatched. What it renders are the
 * presentational components from `@evorait/pace-components`, fed by the view-model factories in this
 * folder, which is where the config-, translation- and material-dependent parts the store slice
 * leaves out are added.
 *
 * Because the store pushes every change, the screen keeps no state of its own: it subscribes to
 * nothing that would re-run its initialization, holds no copy of the notification list — that is a
 * selector — and needs no retry loop waiting for a status to become readable, since store state
 * always carries one.
 */
@Component({
    selector: 'pace-notification-details',
    standalone: true,
    imports: [
        PaceCoreModule,
        SharedModule,
        PaceNotificationDetailNavigationComponent,
        PaceNotificationDetailHeaderComponent,
        PaceNotificationLongtextComponent,
        PaceNotificationDetailCarouselComponent,
        PaceNotificationActionLinksComponent,
        PaceStatusStepButtonComponent,
        PaceStatusBadgeComponent,
    ],
    templateUrl: './notification-details.component.html',
    styleUrls: ['./notification-details.component.scss'],
    animations: [OverviewAnimations.slide],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotificationDetailsComponent implements OnInit {
    /** The flag in front of the user status, which the screen renders next to the projected badge. */
    public readonly faFlag = faFlag;

    private readonly log: LogInterface = inject(LogInstanceToken);
    private readonly store = inject(Store);
    private readonly route = inject(ActivatedRoute);
    private readonly router = inject(Router);
    private readonly destroyRef = inject(DestroyRef);
    private readonly translate = inject(TranslateService);
    private readonly modalService = inject(BsModalService);
    private readonly eventService = inject(EventService);
    private readonly uiResponsiveService = inject(UiResponsiveService);
    private readonly clientManager = inject<ClientManagerInterface>(ClientManagerInstanceToken);
    private readonly windowService = inject(WindowService);
    private readonly materialService = inject(MaterialService);
    private readonly confirmationService = inject(ConfirmationService);
    private readonly notificationModelFactory = inject(NotificationModelFactory);
    private readonly headerFactory = inject(NotificationDetailsHeaderFactory);
    private readonly contentFactory = inject(NotificationDetailsContentFactory);
    private readonly stepButtonFactory = inject(NotificationDetailsStepButtonFactory);
    private readonly modals = inject(NotificationDetailsModalsService);
    private readonly statusStep = inject(NotificationDetailsStatusStepService);
    private readonly completedNotifications$ = completedNotifications$();

    private readonly centricarePCHEnabled = this.store.selectSignal(selectCentricarePCHEnabled);

    /** The notification the route names, re-read whenever the user steps to a neighbouring one. */
    private readonly id$: Observable<NotificationIdState> = this.route.paramMap.pipe(
        map((params) => ({
            frontId: params.get('frontid') ?? '',
            qmNum: params.get('qmnum') ?? '',
            maNum: params.get('manum') ?? '',
        })),
        distinctUntilChanged((previous, current) => NotificationStateUtilities.compare(previous, current)),
    );

    /**
     * `now` is re-read whenever the route changes, i.e. whenever a different notification is shown.
     * Status changes flow through the inner selectors and need no new timestamp.
     */
    private readonly detail$: Observable<NotificationDetailState | undefined> = this.id$.pipe(
        switchMap((id) => this.store.select(selectNotificationDetailById(id, Date.now()))),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    private readonly detail: Signal<NotificationDetailState | undefined> = toSignal(this.detail$, {
        initialValue: undefined,
    });

    /** Which action the step button offers on its main half, once a change has moved it on. */
    private readonly selectedActionId = signal<string | undefined>(undefined);
    /** Whether a status change is in flight — the step button spins and blocks while it is. */
    private readonly isSubmittingStatus = signal(false);
    private readonly region: Signal<Region> = toSignal(this.uiResponsiveService.currentRegion$, {
        initialValue: <Region>'Unknown',
    });
    private readonly isMobile: Signal<boolean> = toSignal(this.uiResponsiveService.isMobile$, { initialValue: false });
    /**
     * Bumped whenever material data changed underneath the carousel, so the view is rebuilt. The
     * materials live outside the notification store, so nothing else would signal it.
     */
    private readonly materialRevision = signal(0);

    public readonly view: Signal<NotificationDetailsView | undefined> = computed(() => {
        const state = this.detail();
        if (!state) {
            return undefined;
        }
        const notification = this.notificationModelFactory.create(state.notificationId);
        if (!notification) {
            return undefined;
        }
        // Registers the revision as a dependency, so material data changing rebuilds the carousel
        // even though the notification store itself did not change — see the field's own note.
        this.materialRevision();

        return {
            header: this.headerFactory.create(state, notification),
            longtext: this.contentFactory.createLongtext(notification, this.isMobile()),
            carousel: this.contentFactory.createCarousel(state, notification, this.region()),
            actionLinks: this.contentFactory.createActionLinks(state),
            stepButton: this.stepButtonFactory.create(state, false, this.isSubmittingStatus(), this.selectedActionId()),
        };
    });

    /** The same button as in the header, rendered in the smartphone bottom bar. */
    public readonly bottomBarStepButton: Signal<PaceStatusStepButton | undefined> = computed(() => {
        const view = this.view();
        return view ? { ...view.stepButton, isDropup: true } : undefined;
    });

    /**
     * The notifications the arrows step through, and where the shown one sits among them. Cancelled
     * notifications the user has not confirmed the cancellation of are dropped by the selector — see
     * {@link selectNotificationNavigationEntries}.
     */
    private readonly navigation$: Observable<{ entries: NotificationNavigationEntry[]; index: number }> = this.id$.pipe(
        switchMap((id) =>
            this.store.select(selectNotificationNavigationEntries(Date.now())).pipe(
                map((entries) => ({
                    entries,
                    index: entries.findIndex((entry) => NotificationStateUtilities.compare(entry.notificationId, id)),
                })),
            ),
        ),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    private readonly navigation = toSignal(this.navigation$, { initialValue: { entries: [], index: -1 } });

    public readonly showPrevious: Signal<boolean> = computed(() => this.navigation().index > 0);
    public readonly showNext: Signal<boolean> = computed(() => {
        const { entries, index } = this.navigation();
        return index >= 0 && index < entries.length - 1;
    });

    /**
     * The direction the next slide animation runs in, set when the user steps to a neighbour and
     * consumed by the animation right after.
     */
    private swipeDirection: ConfigConstants.SWIPE_ACTION = ConfigConstants.SWIPE_ACTION.UNKNOWN;

    public ngOnInit(): void {
        // A notification that is opened has been read. The store's unread flag only applies while
        // the notification is dispatched, so this is a no-op for any later status.
        this.detail$
            .pipe(
                filter((state): state is NotificationDetailState => !!state?.isUnread),
                tap((state) =>
                    this.store.dispatch(
                        updateNotificationStatusFields({
                            id: state.notificationId,
                            changes: { unread: false },
                        }),
                    ),
                ),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe();

        this.materialService.updateUnprocessedMaterials().pipe(take(1)).subscribe();

        this.statusStep.statusSubmitted$
            .pipe(
                tap((statusCode) => {
                    this.selectedActionId.set(statusCode);
                    this.isSubmittingStatus.set(false);
                }),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe();

        // The Safety Assessment form is filled in outside this screen; once it comes back submitted
        // the ATS status is recorded, so the step button moves on by itself.
        this.detail$
            .pipe(
                filter((state): state is NotificationDetailState => !!state),
                switchMap((state) => this.statusStep.trackSafetyAssessmentSubmission(state)),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe();

        this.observeMaterialChanges();
        this.observePchAlerts();
        this.observeCompletedElsewhere();
    }

    // ---- navigation ---------------------------------------------------------------------------

    public onPreviousNotification(): void {
        this.stepToNotification(-1, ConfigConstants.SWIPE_ACTION.RIGHT);
    }

    public onNextNotification(): void {
        this.stepToNotification(1, ConfigConstants.SWIPE_ACTION.LEFT);
    }

    /**
     * @param offset -1 for the previous notification, 1 for the next one
     * @param direction which way the slide animation runs
     */
    private stepToNotification(offset: number, direction: ConfigConstants.SWIPE_ACTION): void {
        const { entries, index } = this.navigation();
        const target = entries[index + offset];
        if (!target) {
            return;
        }
        this.swipeDirection = direction;
        // The step button's label follows the notification, so the previous one's must not stick.
        this.selectedActionId.set(undefined);
        this.router
            .navigate([
                ConfigConstants.routeUrlDetails,
                target.notificationId.frontId,
                target.notificationId.qmNum,
                target.notificationId.maNum,
            ])
            .catch((error: Error) =>
                this.log.error('NotificationDetailsComponent: stepToNotification: router.navigate: ', error),
            );
    }

    public getAnimationData(): AnimationsDataModel {
        const offsetEnter =
            this.swipeDirection === ConfigConstants.SWIPE_ACTION.RIGHT
                ? -SLIDE_OFFSET
                : this.swipeDirection === ConfigConstants.SWIPE_ACTION.LEFT
                  ? SLIDE_OFFSET
                  : 0;
        this.swipeDirection = ConfigConstants.SWIPE_ACTION.UNKNOWN;
        return { value: this.navigation().index, params: { offsetEnter } };
    }

    // ---- actions ------------------------------------------------------------------------------

    public onHeaderAction(action: PaceNotificationDetailHeaderAction): void {
        this.withNotification((state, notification) => {
            switch (action) {
                case 'duration':
                    this.modals.openDuration(notification, state.durationTaskNumber);
                    break;
                case 'customer':
                    this.modals.openCustomer(notification);
                    break;
                case 'marketMap':
                    this.log.warn('NotificationDetailsComponent: the MarketMap link is not offered on this screen');
                    break;
            }
        });
    }

    public onHeaderFieldAction(field: PaceNotificationDetailField): void {
        this.withNotification((state, notification) => {
            switch (field.id) {
                case NOTIFICATION_DETAIL_FIELD.SYSTEM:
                    this.modals.openInternalInfo(notification);
                    break;
                case NOTIFICATION_DETAIL_FIELD.TECHNICAL_ID:
                    this.modals.openObjectInfo(notification);
                    break;
                case NOTIFICATION_DETAIL_FIELD.CONTRACT:
                    this.modals.openContract(notification);
                    break;
                case NOTIFICATION_DETAIL_FIELD.CRITICALITY:
                    this.modals.openCriticality(notification);
                    break;
                case NOTIFICATION_DETAIL_FIELD.APPROVED_PCH:
                    this.modals.openApprovedPchDuration(notification);
                    break;
                default:
                    this.log.warn(`NotificationDetailsComponent: no action for header field ${field.id}`);
            }
        });
    }

    public onActionLinkSelect(link: PaceActionLink): void {
        this.withNotification((state, notification) => {
            switch (link.id) {
                case NOTIFICATION_DETAIL_ACTION.MAINTENANCE_LOCATION:
                    this.modals.openMaintenanceLocation(notification);
                    break;
                case NOTIFICATION_DETAIL_ACTION.SYSTEM_CONFIGURATION:
                    this.modals.openSystemConfiguration(notification);
                    break;
                case NOTIFICATION_DETAIL_ACTION.EQUIPMENT_HISTORY:
                    this.modals.openEquipmentHistory(notification);
                    break;
                case NOTIFICATION_DETAIL_ACTION.EQUIPMENT_CONFIGURATION:
                    this.modals.openEquipmentConfiguration(notification);
                    break;
                case NOTIFICATION_DETAIL_ACTION.EQUIPMENT_OTHER_TASKS:
                    this.modals.openEquipmentOtherTasks(notification);
                    break;
                case NOTIFICATION_DETAIL_ACTION.MATERIAL_REQUEST:
                    this.modals.openMaterialRequest(
                        notification,
                        this.contentFactory.displayMaterialPricing(state),
                        this.contentFactory.displayMaterialQuote(state),
                    );
                    break;
                case NOTIFICATION_DETAIL_ACTION.MATERIAL_QUOTE:
                    this.modals.openMaterialQuote(notification);
                    break;
                case NOTIFICATION_DETAIL_ACTION.MATERIAL_PRICING:
                    this.modals.openMaterialPricing(notification, this.contentFactory.displayMaterialQuote(state));
                    break;
                case NOTIFICATION_DETAIL_ACTION.DOC_BOX:
                    this.modals.openDocBox(notification);
                    break;
                default:
                    this.log.warn(`NotificationDetailsComponent: no action for action link ${link.id}`);
            }
        });
    }

    public onMaterialPickup(item: PaceNotificationMaterialItem): void {
        this.withNotification((state, notification) => {
            const materials = this.contentFactory.getOrderedMaterials(state);
            const material = materials.find(
                (entry) =>
                    `${entry.materialIdentifier.orderNumber.value}:${entry.materialIdentifier.orderPosition.value}:${entry.materialKey}` ===
                    item.id,
            );
            if (!material) {
                this.log.error(`NotificationDetailsComponent: material ${item.id} is no longer known`);
                return;
            }
            this.modals.openMaterialPickup(notification, material, materials, this.isNotReceivedSupported());
        });
    }

    public onParticipantEmail(item: PaceNotificationParticipantItem): void {
        if (item.email) {
            this.windowService.nativeWindow.location.href = `mailto:${item.email}`;
        }
    }

    public onStatusStepAction(action: PaceStatusStepEntry): void {
        this.isSubmittingStatus.set(true);
        this.withNotification((state, notification) => {
            this.statusStep
                .runAction(action, state, notification)
                .catch((error: unknown) =>
                    this.log.error('NotificationDetailsComponent: running the status step action failed', error),
                )
                .finally(() => this.isSubmittingStatus.set(false));
        });
    }

    /**
     * Runs `action` with the notification the screen is showing, or logs that it is gone. Every
     * action needs the same pair, and none of them may run without it.
     *
     * @param action what to do with the notification
     */
    private withNotification(action: (state: NotificationDetailState, notification: NotificationModel) => void): void {
        const state = this.detail();
        const notification = state ? this.notificationModelFactory.create(state.notificationId) : undefined;
        if (!state || !notification) {
            this.log.error('NotificationDetailsComponent: the notification is no longer in the store');
            return;
        }
        action(state, notification);
    }

    /** Reporting a material as not received is a PACE US feature. */
    private isNotReceivedSupported(): boolean {
        return this.clientManager.isNgmP58();
    }

    // ---- reactions to what happens elsewhere ---------------------------------------------------

    /**
     * The materials of a notification are not part of the notification store, so their two change
     * events rebuild the carousel by hand.
     */
    private observeMaterialChanges(): void {
        merge(
            this.eventService.materialsUpdated$,
            this.eventService.refreshMaterialReturnCount$,
            this.materialService.requestedMaterials$,
            this.eventService.materialConsumptionOrNotReceivedChanged$,
        )
            .pipe(
                tap(() => this.materialRevision.update((revision) => revision + 1)),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe();
    }

    /** The PCH reminder is scheduled when work starts on site, and its alert opens from anywhere. */
    private observePchAlerts(): void {
        this.eventService.triggerPCHAlertOnOSR$
            .pipe(
                tap(() => this.withNotification((state, notification) => this.schedulePchReminder(notification))),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe();

        this.eventService.openPCHAlertModal$
            .pipe(
                tap(() => this.withNotification((state, notification) => this.modals.openPchAlert(notification))),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe();
    }

    private schedulePchReminder(notification: NotificationModel): void {
        if (this.centricarePCHEnabled() && notification.isCentriCareContract()) {
            this.modals.schedulePchReminderForOnSite(notification);
        }
    }

    /**
     * Handles a notification that was confirmed on another device: its confirmed status is recorded,
     * and the user is sent back to the dashboard with an explanation rather than left on a screen
     * whose actions would all fail.
     */
    private observeCompletedElsewhere(): void {
        this.completedNotifications$
            .pipe(
                switchMap((completed) => {
                    const state = this.detail();
                    // The legacy completed list is keyed by `NotificationIdentifierModel.toString()`.
                    if (
                        !state ||
                        !completed.completedNotifications[stateToNotificationIdModel(state.notificationId).toString()]
                    ) {
                        return of(undefined);
                    }
                    return this.statusStep
                        .saveConfirmedStatus(state)
                        .pipe(tap(() => this.confirmationService.triggerConfirmationCompletedPreviously()));
                }),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe();

        this.confirmationService.confirmationCompletedPreviously$
            .pipe(
                tap(() => this.leaveForCompletedNotification()),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe();
    }

    private leaveForCompletedNotification(): void {
        const state = this.detail();
        this.router.navigate([ConfigConstants.routeUrlDashboard], {}).catch((error: unknown) => {
            this.log.error(
                `NotificationDetailsComponent: could not open the dashboard after checking completed notifications: ${JSON.stringify(error)}`,
            );
        });
        this.store.dispatch(notificationRefreshRequired());
        this.modalService.show(YesNoModalDialogComponent, {
            backdrop: 'static',
            class: ModalClass.MODAL_MEDIUM,
            initialState: {
                model: new YesNoModalDialogModel(
                    <string>this.translate.instant('CONFIRMATION_ALREADY_COMPLETED_TITLE'),
                    <string>this.translate.instant('CONFIRMATION_ALREADY_COMPLETED_BODY', {
                        notification: state ? stateToNotificationIdModel(state.notificationId).toString() : '',
                    }),
                    <string>this.translate.instant('CONFIRMATION_ALREADY_COMPLETED_OK'),
                ),
            },
            ariaLabelledBy: 'dashboard-button-modal-title',
        });
    }
}
